import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const KpiDrillDownModal = ({ open, onClose, kpiKey, kpiLabel, drillDownType, data }) => {
  
  // Console warning when modal opens with synthetic data
  useEffect(() => {
    if (open && !data) {
      console.warn('⚠️ Drill-down showing PLACEHOLDER DATA. Real calculations pending.');
    } else if (open && data) {
      console.log('📊 Drill-down showing REAL DATA for:', kpiLabel);
    }
  }, [open, data, kpiLabel]);

  // PLACEHOLDER DATA - Clearly marked
  const placeholderTrendData = [
    { period: 'Week 1', value: 92, goal: 90 },
    { period: 'Week 2', value: 94, goal: 90 },
    { period: 'Week 3', value: 93, goal: 90 },
    { period: 'Week 4', value: 97, goal: 90 },
    { period: 'Week 5', value: 96, goal: 90 },
    { period: 'Week 6', value: 98, goal: 90 },
  ];
  
  const placeholderBreakdownData = [
    { platform: 'BNA', value: 95, goal: 90 },
    { platform: 'ATL', value: 92, goal: 90 },
    { platform: 'ASH', value: 98, goal: 90 },
    { platform: 'RDU', value: 94, goal: 90 },
    { platform: 'CAE', value: 91, goal: 90 },
    { platform: 'GSO', value: 89, goal: 90 },
  ];

  // Color for chart lines/bars
  const chartColor = '#1976d2';

  // Process real data if provided, otherwise use synthetic data
  let chartData;
  let showRealDataBanner = false;

  if (data) {
    showRealDataBanner = true;
    console.log('📊 Processing real data:', data);
    chartData = data.data.map(item => {
      if (data.type === 'time_series') {
        // Parse date manually to avoid timezone issues
        const [year, month, day] = item.date.split('-');
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return {
          period: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
          value: item.value,
          name: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
        };
      } else if (data.type === 'time_series_with_volume') {
        // Parse date manually to avoid timezone issues
        const [year, month, day] = item.date.split('-');
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return {
          period: date.toLocaleDateString('en-US', { month: 'short' }),
          name: date.toLocaleDateString('en-US', { month: 'short' }),
          value: item.value,
          volume: item.volume
        };
      } else if (data.type === 'by_platform') {
        return {
          platform: item.platform,
          value: item.value,
          name: item.platform
        };
      }
      return item;
    });
    console.log('📊 Transformed chartData:', chartData);
  } else {
    // Use existing placeholder data
    chartData = drillDownType === 'trend' ? placeholderTrendData : placeholderBreakdownData;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          {kpiLabel} - Drill Down
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        {/* WARNING BANNER - Only show for synthetic data */}
        {!showRealDataBanner && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            ⚠️ SAMPLE DATA - Real calculations in progress. This framework is ready for real data injection.
          </Alert>
        )}
        
        {/* REAL DATA BANNER */}
        {showRealDataBanner && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ REAL DATA - Showing actual {
              data.type === 'time_series' ? 'historical trends' : 
              data.type === 'time_series_with_volume' ? 'volume-weighted historical trends' :
              data.type === 'vendor_list' ? 'vendor list' :
              data.type === 'by_material_group' ? 'inventory breakdown by department' :
              'platform breakdown'
            } for {kpiLabel}
          </Alert>
        )}

        {data?.type === 'time_series_with_volume' ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              📈 Volume-Weighted Historical Trend
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Fill rate over time with dot sizes proportional to monthly volume
            </Typography>
            
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                  label={{ value: 'Fill Rate %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      return (
                        <Box sx={{ bgcolor: 'white', p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
                          <Typography variant="body2">{payload[0].payload.name}</Typography>
                          <Typography variant="body2" color="primary">
                            Fill Rate: {payload[0].value}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Volume: {payload[0].payload.volume?.toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    // Scale dot size based on volume (normalize between volumes)
                    const maxVolume = 880612;
                    const minVolume = 311251;
                    const normalizedSize = ((payload.volume - minVolume) / (maxVolume - minVolume)) * 6 + 4;
                    return <circle cx={cx} cy={cy} r={normalizedSize} fill="#1976d2" />;
                  }}
                  name={kpiLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : data?.type === 'time_series' ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              📈 Historical Trend Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Track performance over time to identify patterns and trends
            </Typography>
            
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                  label={{ value: 'Percentage %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#1976d2" 
                  strokeWidth={3}
                  dot={{ r: 5, fill: "#1976d2" }}
                  name={kpiLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : drillDownType === 'trend' ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              📈 Historical Trend Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Track performance over time to identify patterns and trends
            </Typography>
            
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                  label={{ value: 'Value', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke={chartColor} 
                  strokeWidth={3}
                  dot={{ r: 5, fill: chartColor }}
                  name={kpiLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : data?.type === 'vendor_list' ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              📋 Converted Vendors List
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete list of vendors that have been successfully converted
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {data.data.map((item, index) => (
                <Typography key={index} variant="body1" sx={{ mb: 1, pl: 1 }}>
                  {index + 1}. {item.vendor}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : data?.type === 'by_material_group' ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              📦 Inventory by Department
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Warehouse inventory breakdown by department category
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data.data.map(item => ({
                name: item.department || item.materialGroup, // Support both for backwards compatibility
                value: item.quantity,
                skuCount: item.skuCount
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={120}
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                  label={{ value: 'Quantity', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {data.name}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            Quantity: {data.value.toLocaleString()}
                          </Typography>
                          {data.skuCount && (
                            <Typography variant="body2" color="text.secondary">
                              SKUs: {data.skuCount}
                            </Typography>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#1976d2"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
              🏪 Breakdown by Platform
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Compare performance across different locations and platforms
            </Typography>
            
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="platform" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#ccc' }}
                  label={{ value: 'Value', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="value" 
                  fill={chartColor} 
                  name={kpiLabel}
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}


      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KpiDrillDownModal;